import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hardwareKeys } from '@/services/queryKeys';
import {
  hardwareService,
  type ComPort,
  type DeviceConfig,
  type DeviceKind,
  type DeviceStatus,
  type HardwareStatus,
  type NfcRead,
  type PresetItem,
  type PrintPayload,
  type ScaleReading,
} from '@/services/hardware.service';
import { USE_MOCK } from '@/utils/env';
import { printAuto } from '@/utils/printSettings';

// TODO(WS-06): Remove mock branch once agent runs on cashier machine.
async function mockStatus(): Promise<HardwareStatus> {
  await new Promise((r) => setTimeout(r, 150));
  return {
    agentVersion: 'mock-0.1.0',
    agentRunning: true,
    devices: [
      { kind: 'printer', connected: true, model: 'Xprinter XP-N160II', serial: 'MOCK-PRT-01', port: 'USB001' },
      { kind: 'scale', connected: true, model: 'Ohaus SD200', serial: 'MOCK-SCL-01', port: 'COM3' },
      { kind: 'nfc', connected: false, lastError: 'Chưa kết nối đầu đọc' },
      { kind: 'drawer', connected: true, model: 'Cash drawer #1' },
    ],
  };
}

async function mockScale(): Promise<ScaleReading> {
  await new Promise((r) => setTimeout(r, 400));
  return {
    weightGram: 37.5 + Math.random() * 0.2,
    stable: true,
    unit: 'g',
    readAt: new Date().toISOString(),
  };
}

async function mockNfc(): Promise<NfcRead> {
  await new Promise((r) => setTimeout(r, 600));
  return {
    uid: '04:A2:B1:3C:5D:6E:7F',
    readAt: new Date().toISOString(),
  };
}

async function mockPorts(): Promise<ComPort[]> {
  await new Promise((r) => setTimeout(r, 120));
  return [
    { path: 'COM3', manufacturer: 'Prolific', description: 'USB Serial (Scale)' },
    { path: 'COM4', manufacturer: 'FTDI', description: 'USB Serial' },
    { path: 'USB001', manufacturer: 'Xprinter', description: 'Thermal printer' },
  ];
}

const EMPTY_STATUS: HardwareStatus = {
  agentVersion: 'unknown',
  agentRunning: false,
  devices: [],
};

/**
 * Poll hardware agent status every 10s to keep UI fresh.
 * Normalises shape: nếu BE/agent trả về không đúng HardwareStatus
 * (HTML fallback SPA, null, string), return EMPTY_STATUS để consumer
 * an toàn gọi `.devices.find(...)`.
 */
export function useHardwareStatus(pollMs = 10_000) {
  return useQuery({
    queryKey: hardwareKeys.status(),
    queryFn: async () => {
      try {
        const raw = USE_MOCK ? await mockStatus() : await hardwareService.status();
        if (
          !raw ||
          typeof raw !== 'object' ||
          !Array.isArray((raw as HardwareStatus).devices)
        ) {
          return EMPTY_STATUS;
        }
        return raw as HardwareStatus;
      } catch {
        return EMPTY_STATUS;
      }
    },
    refetchInterval: pollMs,
    retry: 0,
  });
}

export function useComPorts() {
  return useQuery({
    queryKey: [...hardwareKeys.all, 'ports'] as const,
    queryFn: async () => {
      if (USE_MOCK) return mockPorts();
      try {
        return await hardwareService.listAdminPorts();
      } catch {
        return mockPorts(); // safety net khi agent off
      }
    },
    staleTime: 10_000,
  });
}

/** Snapshot config admin (driver, port, baudrate...) — dùng để hydrate
 *  Cấu hình modal trong /settings/hardware. Refetch khi modal mở. */
export function useAdminConfig() {
  return useQuery({
    queryKey: [...hardwareKeys.all, 'adminConfig'] as const,
    queryFn: () => hardwareService.getAdminConfig(),
    staleTime: 5_000,
    retry: 0,
  });
}

/** Preset metadata cho từng kind (verified ✓ + custom ★). Dùng cho dropdown
 *  preset trong Cấu hình modal — chọn preset → auto-fill driver + baud + parity. */
export function useDevicePresets() {
  return useQuery<{
    scale: PresetItem[];
    nfc: PresetItem[];
    receipt_printer: PresetItem[];
    label_printer: PresetItem[];
  }>({
    queryKey: [...hardwareKeys.all, 'presets'] as const,
    queryFn: () => hardwareService.getPresets(),
    staleTime: 60_000,
    retry: 0,
  });
}

export function useReadScale() {
  return useMutation({
    mutationFn: async () => (USE_MOCK ? mockScale() : hardwareService.readScale()),
  });
}

export function useTareScale() {
  return useMutation({
    mutationFn: async () => (USE_MOCK ? { ok: true as const } : hardwareService.tareScale()),
  });
}

/** Alias khớp Jira AC naming — `useScale()` gộp read + tare. */
export function useScale() {
  const read = useReadScale();
  const tare = useTareScale();
  return {
    read: read.mutateAsync,
    tare: tare.mutateAsync,
    isReading: read.isPending,
    isTaring: tare.isPending,
    lastReading: read.data,
  };
}

export function useReadNfc() {
  return useMutation({
    mutationFn: async () => (USE_MOCK ? mockNfc() : hardwareService.readNfc()),
  });
}

/** Alias khớp Jira AC naming. */
export const useNfc = useReadNfc;

/**
 * Print router — auto-route theo setting localStorage (xem
 * `@/utils/printSettings`):
 *   - 'agent'  → raw bytes qua hardware agent (TCP/USB)
 *   - 'browser' → render HTML + window.print()
 *
 * User toggle ở /settings/hardware. Setting lưu per máy.
 *
 * Cho non-React caller (event listener, util) dùng `printAuto()` từ
 * `@/utils/printSettings` — cùng API, không cần hook context.
 */
export function usePrint() {
  return useMutation({
    mutationFn: async (body: PrintPayload) => {
      if (USE_MOCK) return { ok: true as const };
      return printAuto(body);
    },
  });
}

/**
 * Mở ngăn kéo tiền — agent CHƯA có endpoint riêng. Trên thực tế ngăn kéo
 * thường nối qua máy in nhiệt và mở qua ESC/POS kick command sau khi in
 * receipt (xem `PrintPayload.openDrawerAfter`). Hook này giữ no-op +
 * report `lastError` để UI không silent-success khi agent thật offline.
 */
export function useOpenDrawer() {
  return useMutation({
    mutationFn: async (): Promise<{ ok: boolean; lastError?: string }> => {
      if (USE_MOCK) return { ok: true };
      return {
        ok: false,
        lastError: 'Agent chưa hỗ trợ mở ngăn kéo độc lập — dùng print receipt với openDrawerAfter=true',
      };
    },
  });
}

/** Back-compat alias (EG-83 uses usePrint với PrintPayload). */
export const usePrintReceipt = usePrint;

/**
 * In bản test — gọi agent `/admin/api/test/printer/{id}` (driver test_print).
 * Trả {ok, error} chuẩn cho UI; phân biệt được driver fail vs printer offline.
 */
export function usePrintTestPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ ok: boolean; lastError?: string }> => {
      if (USE_MOCK) return { ok: true };
      try {
        const r = await hardwareService.testPrinter();
        return { ok: r.success, lastError: r.error };
      } catch (e: unknown) {
        return { ok: false, lastError: (e as Error)?.message ?? 'agent offline' };
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: hardwareKeys.status() }),
  });
}

/**
 * Per-device ping — gọi agent admin test endpoint cho từng kind. Driver thật
 * sẽ thử open port + read; demo MockDriver luôn trả OK. Surface error message
 * từ ScaleDeviceUnavailableError / NfcDeviceUnavailableError.
 */
export function usePingDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (kind: DeviceKind): Promise<{ ok: boolean; lastError?: string }> => {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        if (kind === 'nfc') return { ok: false, lastError: 'Chưa kết nối đầu đọc' };
        return { ok: true };
      }
      try {
        if (kind === 'scale') {
          const r = await hardwareService.testScale();
          return { ok: r.success, lastError: r.error };
        }
        if (kind === 'receipt_printer' || kind === 'printer') {
          const r = await hardwareService.testReceiptPrinter();
          return { ok: r.success, lastError: r.error };
        }
        if (kind === 'label_printer') {
          const r = await hardwareService.testLabelPrinter();
          return { ok: r.success, lastError: r.error };
        }
        if (kind === 'nfc') {
          const r = await hardwareService.testNfc();
          return { ok: r.success, lastError: r.error };
        }
        // 'drawer' — không có endpoint riêng (mở qua receipt printer ESC/POS kick).
        const r = await hardwareService.testReceiptPrinter();
        return { ok: r.success, lastError: r.error };
      } catch (e: unknown) {
        return { ok: false, lastError: (e as Error)?.message ?? 'agent offline' };
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: hardwareKeys.status() }),
  });
}

/**
 * Full device config payload — match AdminConfigPayload trong agent app/api/admin.py.
 * Mỗi field optional; chỉ field non-undefined được PUT lên agent.
 */
export interface FullDeviceConfig {
  // Scale
  scale_driver?: string;
  scale_port?: string | null;
  scale_baudrate?: number | null;
  scale_databits?: number | null;
  scale_parity?: string;
  scale_stopbits?: number | null;
  scale_only_stable?: boolean;
  // Receipt printer (máy in nhiệt)
  receipt_driver?: string;
  receipt_host?: string | null;
  receipt_tcp_port?: number | null;
  receipt_usb_path?: string | null;
  // Label printer (máy in tem)
  label_driver?: string;
  label_host?: string | null;
  label_tcp_port?: number | null;
  label_usb_path?: string | null;
  // NFC
  nfc_driver?: string;
  nfc_port?: string | null;
  nfc_baudrate?: number | null;
}

/**
 * Cấu hình thiết bị — PUT /admin/api/config với field theo kind. Sau khi
 * agent save xong, registry hot-reload → status + adminConfig query refetch
 * để FE thấy driver/port mới ngay (không cần restart agent).
 *
 * 2 dạng input:
 * - Legacy `{ kind, config: { model, port } }` — back-compat.
 * - Mới: trực tiếp truyền `FullDeviceConfig` với field admin (driver, baudrate, ...).
 */
export function useConfigDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input:
        | { kind: DeviceKind; config: DeviceConfig }
        | { kind?: DeviceKind; full: FullDeviceConfig },
    ): Promise<DeviceStatus> => {
      if (USE_MOCK) {
        await new Promise((r) => setTimeout(r, 200));
        const k = ('kind' in input && input.kind) || 'scale';
        return { kind: k, connected: true };
      }
      let body: Record<string, unknown> = {};
      if ('full' in input) {
        body = Object.fromEntries(
          Object.entries(input.full).filter(([, v]) => v !== undefined),
        );
      } else {
        const { kind, config } = input;
        if (kind === 'scale') {
          if (config.model) body.scale_driver = config.model;
          if (config.port !== undefined) body.scale_port = config.port || null;
        } else if (kind === 'receipt_printer' || kind === 'printer') {
          if (config.model) body.receipt_driver = config.model;
          if (config.port !== undefined) body.receipt_usb_path = config.port || null;
        } else if (kind === 'label_printer') {
          if (config.model) body.label_driver = config.model;
          if (config.port !== undefined) body.label_usb_path = config.port || null;
        } else if (kind === 'nfc') {
          if (config.model) body.nfc_driver = config.model;
          if (config.port !== undefined) body.nfc_port = config.port || null;
        }
      }
      if (Object.keys(body).length > 0) {
        await hardwareService.updateConfig(body);
      }
      const kind = 'kind' in input && input.kind ? input.kind : 'scale';
      return { kind, connected: true };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hardwareKeys.status() });
      qc.invalidateQueries({ queryKey: [...hardwareKeys.all, 'adminConfig'] });
    },
  });
}
